import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Lock, CreditCard, Home, LogIn, LogOut, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fetchBillingSubscription } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ACTIVE_STATUSES = new Set(["active", "trialing", "grandfathered"]);

export default function SubscriptionGuard({ children }) {
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const [state, setState] = useState({ loading: true, allowed: false, status: "", plan: "" });

    const handleLogout = () => {
        logout();
        navigate("/login", { replace: true });
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const data = await fetchBillingSubscription();
                if (cancelled) return;
                const status = data.subscription_status || "";
                setState({
                    loading: false,
                    allowed: ACTIVE_STATUSES.has(status),
                    status,
                    plan: data.plan || "",
                });
            } catch (err) {
                if (cancelled) return;
                // 402 = payment required; treat as not allowed but show paywall.
                // Other errors → fail open so a backend hiccup doesn't lock everyone out.
                if (err?.response?.status === 402) {
                    setState({ loading: false, allowed: false, status: "none", plan: "" });
                } else {
                    setState({ loading: false, allowed: true, status: "unknown", plan: "" });
                }
            }
        })();
        return () => { cancelled = true; };
    }, []);

    if (state.loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center" data-testid="subscription-guard-loading">
                <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            </div>
        );
    }

    if (state.allowed) {
        return children;
    }

    const statusLabel = {
        none: "Intet aktivt abonnement",
        canceled: "Annulleret",
        past_due: "Forfalden betaling",
        incomplete: "Betaling ufuldstændig",
        unpaid: "Ubetalt",
    }[state.status] || `Status: ${state.status || "ukendt"}`;

    return (
        <div
            data-testid="subscription-paywall"
            className="min-h-[calc(100vh-4rem)] flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50/30 to-purple-50/30 px-4 py-12"
        >
            <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl shadow-slate-900/5 border border-slate-200/70 p-8 text-center">
                <div className="w-14 h-14 rounded-full bg-amber-100 text-amber-600 inline-flex items-center justify-center">
                    <Lock className="w-7 h-7" />
                </div>
                <h1 className="mt-5 text-2xl font-bold text-slate-900">Dit dashboard er låst</h1>
                <p className="mt-2 text-slate-600">
                    For at få adgang til MyServiceHelper skal du have et aktivt abonnement.
                </p>
                <p className="mt-3 inline-flex items-center px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-medium">
                    {statusLabel}
                </p>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Button
                        data-testid="paywall-pricing-btn"
                        onClick={() => navigate("/priser")}
                        className="h-11 px-6 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-md"
                    >
                        <CreditCard className="w-4 h-4 mr-2" />
                        Vælg abonnement
                    </Button>
                    <Button
                        data-testid="paywall-contact-btn"
                        onClick={() => navigate("/kontakt")}
                        variant="outline"
                        className="h-11 px-6 rounded-full"
                    >
                        <MessageSquare className="w-4 h-4 mr-2" />
                        Kontakt salg
                    </Button>
                </div>

                <div className="mt-5 pt-5 border-t border-slate-100 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm">
                    <button
                        data-testid="paywall-home-link"
                        onClick={() => navigate("/")}
                        className="inline-flex items-center text-slate-500 hover:text-slate-900 transition-colors"
                    >
                        <Home className="w-3.5 h-3.5 mr-1.5" />
                        Til forsiden
                    </button>
                    {user ? (
                        <>
                            <button
                                data-testid="paywall-switch-account-link"
                                onClick={handleLogout}
                                className="inline-flex items-center text-slate-500 hover:text-slate-900 transition-colors"
                            >
                                <LogIn className="w-3.5 h-3.5 mr-1.5" />
                                Skift konto
                            </button>
                            <button
                                data-testid="paywall-logout-link"
                                onClick={handleLogout}
                                className="inline-flex items-center text-slate-500 hover:text-red-600 transition-colors"
                            >
                                <LogOut className="w-3.5 h-3.5 mr-1.5" />
                                Log ud
                            </button>
                        </>
                    ) : (
                        <button
                            data-testid="paywall-login-link"
                            onClick={() => navigate("/login")}
                            className="inline-flex items-center text-slate-500 hover:text-slate-900 transition-colors"
                        >
                            <LogIn className="w-3.5 h-3.5 mr-1.5" />
                            Log ind
                        </button>
                    )}
                </div>

                <p className="mt-5 text-xs text-slate-400">
                    Har du lige betalt? Det kan tage et øjeblik før din konto opdateres — opdatér siden om lidt.
                </p>
            </div>
        </div>
    );
}
